import { useLatestRequest } from '@/hooks/use-latest-request'
import { getErrorMessage } from '@/lib/utils'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { FIELD_NAME } from './delivery-area-rule-utils'

const buildAddressFromMapPoint = (point, geocodedAddress) => {
  const street = [geocodedAddress?.street_number, geocodedAddress?.route]
    .filter(Boolean)
    .join(' ')
    .trim()
  const isRooftop =
    geocodedAddress?.locationType === 'ROOFTOP' && geocodedAddress?.partialMatch !== true

  return {
    city: geocodedAddress?.locality || geocodedAddress?.neighborhood || '',
    country: geocodedAddress?.country || 'US',
    formattedAddress: geocodedAddress?.formattedAddress || '',
    google_place_id: geocodedAddress?.placeId || null,
    is_rooftop: isRooftop,
    is_verified: true,
    lat: point.lat,
    line1: street || geocodedAddress?.formattedAddress || '',
    line2: geocodedAddress?.subpremise || '',
    lng: point.lng,
    postal_code: geocodedAddress?.postal_code || '',
    state: geocodedAddress?.administrative_area_level_1 || '',
  }
}

export const formatMiles = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Unavailable'
  }

  const miles = Number(value)

  return Number.isFinite(miles) ? `${miles.toFixed(2)} mi` : 'Unavailable'
}

export const formatTestAddress = (address, point) => {
  if (address?.formattedAddress) {
    return address.formattedAddress
  }

  const cityLine = [address?.city, address?.state, address?.postal_code].filter(Boolean).join(' ')
  const display = [address?.line1, cityLine].filter(Boolean).join(', ')

  return display || `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`
}

export const feeSourceLabels = {
  boundary_fallback: 'Delivery boundary fallback',
  city: 'City fee',
  distance_range: 'Driving distance range',
  excluded_zone: 'No-delivery area',
  fallback_city: 'City fallback',
  fallback_postal_code: 'ZIP fallback',
  postal_code: 'ZIP fee',
  special_price_zone: 'Special price place',
}

export function useDeliveryMapTester({
  form,
  geocodeLatLng,
  isReadOnly,
  locationId,
  previewDeliveryFee,
}) {
  const [isPointTestMode, setIsPointTestMode] = useState(false)
  const [deliveryTest, setDeliveryTest] = useState(null)
  const { beginLatestRequest, cancelPendingRequests, isLatestRequest } = useLatestRequest()

  const clearPointTest = useCallback(() => {
    cancelPendingRequests()
    setIsPointTestMode(false)
    setDeliveryTest(null)
  }, [cancelPendingRequests])

  const startPointTest = useCallback(() => {
    setIsPointTestMode(true)
  }, [])

  const testPoint = useCallback(
    async (point) => {
      if (!locationId || isReadOnly) {
        return
      }

      const requestId = beginLatestRequest()
      setDeliveryTest({
        point,
        status: 'loading',
      })

      try {
        const geocodedAddress = await geocodeLatLng(point)
        const address = buildAddressFromMapPoint(point, geocodedAddress)
        const result = await previewDeliveryFee({
          address,
          delivery_area_fees: form.getFieldValue(FIELD_NAME) ?? [],
          delivery_city_fees: form.getFieldValue('delivery_city_fees') ?? [],
          delivery_distance_fee_ranges: form.getFieldValue('delivery_distance_fee_ranges') ?? [],
          is_delivery_cutoff_overrides_enabled: form.getFieldValue(
            'is_delivery_cutoff_overrides_enabled'
          ),
          is_distance_pricing_enabled: form.getFieldValue('is_distance_pricing_enabled'),
          delivery_max_distance_miles: form.getFieldValue('delivery_max_distance_miles'),
          delivery_postal_code_fees: form.getFieldValue('delivery_postal_code_fees') ?? [],
          locationId,
        })

        if (!isLatestRequest(requestId)) {
          return
        }

        setDeliveryTest({
          address,
          point,
          result,
          status: 'success',
        })
      } catch (error) {
        if (!isLatestRequest(requestId)) {
          return
        }

        const message = getErrorMessage(error)
        setDeliveryTest({
          error: message,
          point,
          status: 'error',
        })
        toast.error('Could not test that map point', {
          description: message,
        })
      }
    },
    [
      beginLatestRequest,
      form,
      geocodeLatLng,
      isLatestRequest,
      isReadOnly,
      locationId,
      previewDeliveryFee,
    ]
  )

  return {
    clearPointTest,
    deliveryTest,
    isPointTestMode,
    startPointTest,
    testPoint,
  }
}
